/* ═══════════════════════════════════════════════════════════════
   PiARIS — DocMaya Showcase
   Emerald → Teal gradient. Animated toggle, document SVG,
   syntax-highlighted embed code, 4-step deployment flow.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { PRODUCT_GRADIENTS } from "./gradients";
import { ProductLockup } from "./ProductLockup";

var E = React.createElement;
var G = PRODUCT_GRADIENTS.docmaya;

/** Animated toggle switch — slides on/off via CSS animation */
function AnimatedToggle() {
  return E("div", {
    "aria-hidden": "true",
    style: {
      width: 80, height: 40,
      borderRadius: 20,
      background: "rgba(255,255,255,0.18)",
      border: "1.5px solid rgba(255,255,255,0.4)",
      position: "relative",
      flexShrink: 0
    },
    className: "dm-toggle"
  },
    E("div", {
      className: "dm-toggle-thumb",
      style: {
        position: "absolute",
        top: 3,
        left: 3,
        width: 32, height: 32,
        borderRadius: "50%",
        background: "#FFFFFF",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
      }
    })
  );
}

/** Document with code lines SVG illustration */
function DocSvg() {
  return E("svg", {
    width: 120, height: 140, viewBox: "0 0 120 140",
    "aria-hidden": "true",
    style: { filter: "drop-shadow(0 4px 16px rgba(255,255,255,0.2))" }
  },
    /* Document outline */
    E("path", {
      d: "M20 8 L80 8 L100 28 L100 132 L20 132 Z",
      fill: "rgba(255,255,255,0.95)",
      stroke: "rgba(255,255,255,0.5)",
      strokeWidth: 1.5
    }),
    /* Folded corner */
    E("path", {
      d: "M80 8 L80 28 L100 28 Z",
      fill: "rgba(110,231,183,0.6)"
    }),
    /* Code/text lines */
    [
      { y: 42, w: 60, c: "#059669" },
      { y: 52, w: 50, c: "#94a3b8" },
      { y: 62, w: 65, c: "#94a3b8" },
      { y: 72, w: 45, c: "#94a3b8" },
      { y: 88, w: 60, c: "#06B6D4" },
      { y: 98, w: 55, c: "#94a3b8" },
      { y: 108, w: 50, c: "#94a3b8" }
    ].map(function(l, i) {
      return E("rect", {
        key: i,
        x: 30, y: l.y, width: l.w, height: 4,
        rx: 1, fill: l.c, opacity: 0.8
      });
    }),
    /* Checkmark badge */
    E("circle", { cx: 88, cy: 116, r: 14, fill: "#059669" }),
    E("path", {
      d: "M82 116 l4 4 l8 -8",
      stroke: "#fff", strokeWidth: 2.5, fill: "none",
      strokeLinecap: "round", strokeLinejoin: "round"
    })
  );
}

/** Syntax-highlighted embed code block */
function EmbedCode() {
  return E("figure", {
    style: { margin: 0, marginTop: 0 }
  },
    E("figcaption", {
      style: {
        fontSize: 10,
        fontWeight: 700,
        color: "rgba(255,255,255,0.8)",
        textTransform: "uppercase",
        letterSpacing: ".06em",
        marginBottom: 6
      }
    }, "Embed Snippet"),
    E("pre", {
      style: {
        background: "rgba(15,23,42,0.7)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 10,
        padding: "12px 14px",
        margin: 0,
        fontSize: 11,
        fontFamily: "'IBM Plex Mono', monospace",
        lineHeight: 1.6,
        color: "#E2E8F0",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch"
      }
    },
      E("code", null,
        E("span", { style: { color: "#94A3B8" } }, "<!-- DocMaya: 1-line PDF accessibility -->"),
        "\n",
        E("span", { style: { color: "#F472B6" } }, "<script "),
        E("span", { style: { color: "#FBBF24" } }, "src"),
        E("span", { style: { color: "#94A3B8" } }, "="),
        E("span", { style: { color: "#6EE7B7" } }, '"https://cdn.docmaya.io/v1.js"'),
        "\n        ",
        E("span", { style: { color: "#FBBF24" } }, "data-key"),
        E("span", { style: { color: "#94A3B8" } }, "="),
        E("span", { style: { color: "#6EE7B7" } }, '"dm_pk_live_..."'),
        E("span", { style: { color: "#F472B6" } }, "></script>")
      )
    )
  );
}

/** 4-step deployment flow */
function DeploySteps() {
  var steps = [
    { n: 1, t: "Get Key",     d: "Sign in, copy your API key" },
    { n: 2, t: "Add Script",  d: "One <script> tag in <head>" },
    { n: 3, t: "Deploy",      d: "Push to production" },
    { n: 4, t: "Accessible",  d: "PDFs become HTML on toggle" }
  ];
  return E("ol", {
    "aria-label": "Deployment steps",
    style: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 8,
      listStyle: "none",
      padding: 0,
      margin: 0
    },
    className: "dm-steps"
  },
    steps.map(function(s) {
      return E("li", {
        key: s.n,
        style: {
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 10,
          padding: "10px 8px",
          textAlign: "center",
          minHeight: 44
        }
      },
        E("div", {
          "aria-hidden": "true",
          style: {
            width: 22, height: 22,
            borderRadius: "50%",
            background: "#FFFFFF",
            color: G.pri,
            fontSize: 12,
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 6px"
          }
        }, s.n),
        E("div", {
          style: {
            fontSize: 11,
            fontWeight: 800,
            color: "#fff",
            marginBottom: 2
          }
        }, s.t),
        E("div", {
          style: {
            fontSize: 9,
            color: "rgba(255,255,255,0.7)",
            lineHeight: 1.35
          }
        }, s.d)
      );
    })
  );
}

/** DocMayaShowcase — full marketing block */
function DocMayaShowcase(p) {
  var ctaLabel = p.ctaLabel || "Manage DocMaya";
  return E("section", {
    role: "region",
    "aria-labelledby": "dm-showcase-title",
    style: {
      borderRadius: 18,
      overflow: "hidden",
      border: "2px solid " + G.border + "30",
      background: G.hero,
      color: "#FFFFFF",
      position: "relative",
      boxShadow: "0 10px 40px " + G.glow
    }
  },
    /* Header */
    E("div", {
      style: {
        position: "relative",
        zIndex: 1,
        padding: "28px 28px 20px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 20,
        alignItems: "center"
      },
      className: "dm-header"
    },
      E("div", null,
        E("div", {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10
          }
        },
          E(ProductLockup, { size: "small", white: true }),
          E("span", { "aria-hidden": "true", style: { color: "rgba(255,255,255,0.4)" } }, "/"),
          E("span", {
            style: {
              fontSize: 12,
              fontWeight: 700,
              color: "rgba(255,255,255,0.85)",
              textTransform: "uppercase",
              letterSpacing: ".08em"
            }
          }, "DocMaya")
        ),
        E("h2", {
          id: "dm-showcase-title",
          style: {
            fontSize: 26,
            fontWeight: 900,
            margin: 0,
            lineHeight: 1.2,
            letterSpacing: "-0.4px"
          }
        }, "Embeddable PDF Accessibility \u2014 In One Line"),
        E("p", {
          style: {
            fontSize: 13,
            color: "rgba(255,255,255,0.82)",
            margin: "8px 0 0",
            lineHeight: 1.55,
            maxWidth: 480
          }
        }, "End users flip a toggle. PDFs become accessible HTML instantly. Small PDFs remediate live in the browser, large PDFs are pre-processed and served from cache."),
        /* Toggle + label inline */
        E("div", {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 14
          }
        },
          E(AnimatedToggle, null),
          E("span", {
            style: { fontSize: 12, fontWeight: 700, color: "#fff" }
          }, "Make this PDF accessible")
        )
      ),
      /* Doc SVG */
      E("div", { style: { flexShrink: 0 } },
        E(DocSvg, null)
      )
    ),
    /* Body */
    E("div", {
      style: {
        position: "relative",
        zIndex: 1,
        padding: "0 28px 28px",
        display: "flex",
        flexDirection: "column",
        gap: 14
      },
      className: "dm-body"
    },
      E(EmbedCode, null),
      E(DeploySteps, null),
      /* CTA */
      E("div", {
        style: {
          marginTop: 4,
          display: "flex",
          gap: 10,
          flexWrap: "wrap"
        }
      },
        p.onCta ? E("button", {
          onClick: p.onCta,
          style: {
            minHeight: 44,
            padding: "10px 22px",
            borderRadius: 10,
            border: "none",
            background: "#FFFFFF",
            color: G.pri,
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
          }
        }, "\uD83D\uDCC4 " + ctaLabel) : null
      )
    )
  );
}

export { DocMayaShowcase };
